/**
 * Der Abschnitt „Tracking / Stationen" (`ship-stationen`) - die Stufen des Zeitstrahls
 * (`GET /api/orders/:id/zeitstrahl`, `backend/src/dsv/zeitstrahl.ts`), zugeklappt (siehe
 * `Abschnitt.tsx#SEC_COLLAPSED["ship-stationen"]`).
 *
 * Neue Flaeche ohne Vorbild in `index.html` - dort leitet der Zeitstrahl seinen Fortschritt allein
 * aus dem Board-Status ab (`TimelinePage/Stationsspur.tsx`, Verhaltensaenderung 6, eine ANDERE
 * Flaeche mit einem anderen Datenmodell: `Sendungsstation`/`Spurfortschritt` statt der hier
 * verwendeten `Zeitstrahlstufe`). Kein Baseline-Vergleich, kein Uebernahmezwang - siehe
 * Ausnahmeregister (`test/visual/ausnahmen.ts`, Eintrag „Drawer/SpeditionsReiter").
 *
 * Feature "See/Flug-Umschaltung" (Spec 2026-08-26-spedition-see-flug-design.md, E4): im Modus
 * "air" gibt es keine Stationsdaten, aus denen sich eine Stufe ableiten liesse - statt der Liste
 * steht hier der manuell gesetzte Status (order_extras.air_status).
 *
 * ERGAENZUNG (Auftrag "Manueller 3-Stufen-Zeitstrahl fuer Flug-Sendungen", 2026-08-27, Spec docs/
 * superpowers/specs/2026-08-27-auftrag-luft-zeitstrahl-stufen.md): ADDITIV zum `air_status`-
 * Dropdown, das unveraendert stehen bleibt (Status-Zeile unten). Antwort von Damian Gembries
 * (Asana-Ticket „Zeitstrahl bei Flug-Sendungen", 27.08.2026): eine horizontale 3-Punkte-Linie
 * ETD (Abflug Flughafen CN) -> ETA (Ankunft Flughafen Frankfurt) -> ABSINA (Ankunft im Lager),
 * jede Station manuell bestaetigt samt Datum. Die drei Stationen sind HIER lokal definiert
 * (`AIR_STUFEN` unten), NICHT in `FlugSendung.tsx` - jene Datei ist per eigenem Kopfkommentar eine
 * 1:1-Portierung aus `index.html`, deren Wert-Exporte `test/reference/herkunft.test.ts` auf eine
 * Herkunftsmarke prueft; ein hier neu erfundener Wert machte jenen Test deterministisch rot.
 * `Stationen.tsx` selbst ist eine neue Flaeche ohne Vorbild (siehe Kopfkommentar oben) - dort ist
 * das unproblematisch. KEINE Reihenfolge-Sperre: Damian hat nur „Station bestaetigen + Datum
 * eintragen" verlangt, keine Sequenzpflicht - jede der drei Stationen ist unabhaengig editierbar,
 * auch nachtraeglich korrigierbar (ein neues Datum ueberschreibt das alte einfach).
 *
 * DRITTE Transportart "road" (Asana-Ticket 1218176179911324, "Zusaetzlicher LKW Speditionstyp",
 * reine Handpflege wie "air", ohne DSV-Anbindung): derselbe Bauplan wie der "air"-Zweig oben, aber
 * mit NUR ZWEI Stationen (`ROAD_STUFEN` unten) statt drei - der LKW faehrt Lieferant -> Lager, es
 * gibt keinen Zwischenflughafen. Die vormals air-spezifische innere Komponente/der Typ (frueher
 * `AirStufePunkt`/`AirStufe`) sind dafuer VERALLGEMEINERT statt kopiert: `HandStufePunkt`/
 * `HandStufe`, `feld` jetzt die Vereinigung aller fuenf Handfelder beider Transportarten. Verhalten
 * (lokaler Eingabezustand, Bestaetigen/Aendern/Abbrechen, Klassen) bleibt unveraendert - nur Name und
 * Typ wurden geweitet.
 *
 * Der Haken „Liefertermine fixiert" steht NICHT mehr hier, sondern im Abschnitt „Transportdaten"
 * (`SpeditionTab.tsx`) - er gehoert zu den Terminen, nicht zur Stationsanzeige.
 */
import { useState } from "react";
import { fmtDate } from "../../../lib/format.ts";
import { AIR_STATUS_OPTIONEN } from "./FlugSendung.tsx";
import { ROAD_STATUS_OPTIONEN } from "./StrassenSendung.tsx";
import type { Zeitstrahl } from "../../../lib/types.ts";

export interface StationenProps {
  /** `null` heisst: noch nicht geladen. */
  zeitstrahl?: Zeitstrahl | null;
  transportMode?: "see" | "air" | "road";
  airStatus?: "gebucht" | "unterwegs" | "angekommen";
  /** order_extras.air_etd - Abflug Flughafen CN, manuell bestaetigtes Datum (ISO-8601). */
  airEtd?: string | null;
  /** order_extras.air_eta - Ankunft Flughafen Frankfurt, manuell bestaetigtes Datum (ISO-8601). */
  airEta?: string | null;
  /** order_extras.air_absina - Ankunft im Lager, manuell bestaetigtes Datum (ISO-8601). */
  airAbsina?: string | null;
  roadStatus?: "gebucht" | "unterwegs" | "angekommen";
  /** order_extras.road_etd - Abfahrt beim Lieferanten, manuell bestaetigtes Datum (ISO-8601). */
  roadEtd?: string | null;
  /** order_extras.road_eta - Ankunft im Lager, manuell bestaetigtes Datum (ISO-8601). */
  roadEta?: string | null;
  /** Fuer den Schreibweg der Stationen unten (`onAendern(id, { [feld]: wert })`). */
  id?: string;
  onAendern?: (id: string, aenderung: Record<string, unknown>) => void;
}

interface HandStufe {
  feld: "airEtd" | "airEta" | "airAbsina" | "roadEtd" | "roadEta";
  label: string;
  hint: string;
}

/** Die drei manuellen Stationen der Flug-Sendung, in Damians Reihenfolge ETD -> ETA -> ABSINA. */
const AIR_STUFEN: readonly HandStufe[] = [
  { feld: "airEtd", label: "ETD", hint: "Abflug Flughafen CN" },
  { feld: "airEta", label: "ETA", hint: "Ankunft Flughafen Frankfurt" },
  { feld: "airAbsina", label: "ABSINA", hint: "Ankunft im Lager" },
];

/**
 * Die zwei manuellen Stationen der Straßen-Sendung - bewusst ZWEI statt drei (wie bei `AIR_STUFEN`):
 * der LKW faehrt Lieferant -> Lager, es gibt keinen Zwischenflughafen.
 */
const ROAD_STUFEN: readonly HandStufe[] = [
  { feld: "roadEtd", label: "Abfahrt", hint: "Abfahrt beim Lieferanten" },
  { feld: "roadEta", label: "ABSINA", hint: "Ankunft im Lager" },
];

interface HandStufePunktProps {
  stufe: HandStufe;
  wert?: string | null;
  id?: string;
  onAendern?: (id: string, aenderung: Record<string, unknown>) => void;
}

/**
 * EIN Punkt der Punkte-Linie (drei bei Flug, zwei bei Straße). Eigene Komponente wegen des lokalen
 * Eingabezustands (das getippte, noch nicht bestaetigte Datum) - unabhaengige Instanzen nebeneinander,
 * kein gemeinsamer Zustand in `Stationen` noetig.
 */
function HandStufePunkt({ stufe, wert, id, onAendern }: HandStufePunktProps) {
  const [eingabe, setEingabe] = useState("");
  const [bearbeiten, setBearbeiten] = useState(false);
  const erreicht = !!wert;
  // Bereits bestaetigt, aber gerade per "Ändern" wieder geoeffnet - dann zaehlt die Eingabe, nicht
  // der alte Anzeigezweig. Ein frisch geoeffnetes Feld beginnt vorbefuellt mit dem alten Wert
  // (siehe `aendern` unten), Bestaetigen ueberschreibt ihn wie beim Ersteintrag.
  const zeigeEingabe = !erreicht || bearbeiten;

  const aendern = (): void => {
    setEingabe(wert ?? "");
    setBearbeiten(true);
  };

  const bestaetigen = (): void => {
    if (!eingabe || !id || !onAendern) return;
    onAendern(id, { [stufe.feld]: eingabe });
    setBearbeiten(false);
  };

  // Nachbesserung 2, Punkt W1: ohne diesen Knopf gibt es bei leer geloeschter Eingabe (native
  // Loeschgeste des Date-Pickers) keinen Weg zurueck in die Anzeige - "Bestaetigen" bleibt disabled,
  // `bearbeiten` wird nur dort zurueckgesetzt. Abbrechen schreibt nichts, verwirft nur die lokale
  // Eingabe und zeigt wieder den gespeicherten Wert.
  const abbrechen = (): void => {
    setBearbeiten(false);
  };

  return (
    <div
      className={`ship-air-punkt ${erreicht ? "ship-station-erreicht" : "ship-station-offen"}`}
      title={stufe.hint}
    >
      <span className="ship-station-label">{stufe.label}</span>
      <div className="ship-air-punkt-linie">
        <span className="ship-air-punkt-marker" aria-hidden="true" />
      </div>
      {zeigeEingabe ? (
        <div className="ship-air-punkt-eingabe">
          <input
            className="ship-air-punkt-datum"
            type="date"
            aria-label={`${stufe.label}-Datum`}
            value={eingabe}
            onChange={(e) => setEingabe(e.currentTarget.value)}
          />
          <button
            type="button"
            className="ship-air-punkt-knopf ship-air-punkt-knopf-haupt"
            onClick={bestaetigen}
            disabled={!eingabe || !id || !onAendern}
          >
            Bestätigen
          </button>
          {bearbeiten ? (
            <button type="button" className="ship-air-punkt-knopf" onClick={abbrechen}>
              Abbrechen
            </button>
          ) : null}
        </div>
      ) : (
        <div className="ship-air-punkt-eingabe">
          <span className="ship-station-datum">{fmtDate(wert)}</span>
          <button type="button" className="ship-air-punkt-knopf" onClick={aendern}>
            Ändern
          </button>
        </div>
      )}
    </div>
  );
}

export function Stationen({
  zeitstrahl,
  transportMode,
  airStatus,
  airEtd,
  airEta,
  airAbsina,
  roadStatus,
  roadEtd,
  roadEta,
  id,
  onAendern,
}: StationenProps) {
  if (transportMode === "air" || transportMode === "road") {
    const istAir = transportMode === "air";
    const stufen = istAir ? AIR_STUFEN : ROAD_STUFEN;
    const statusOptionen = istAir ? AIR_STATUS_OPTIONEN : ROAD_STATUS_OPTIONEN;
    const status = istAir ? airStatus : roadStatus;
    const label = statusOptionen.find((s) => s.id === status)?.label;
    // EIN Objekt aus allen fuenf Handfeldern - keine Fuell-Eintraege noetig: `stufen` (AIR_STUFEN
    // bzw. ROAD_STUFEN) referenziert je Modus ohnehin nur die drei bzw. zwei tatsaechlich passenden
    // Felder, die uebrigen Werte werden schlicht nicht gelesen.
    const werte: Record<HandStufe["feld"], string | null | undefined> = {
      airEtd,
      airEta,
      airAbsina,
      roadEtd,
      roadEta,
    };
    return (
      <>
        <div className="ship-air-linie">
          {stufen.map((stufe) => (
            <HandStufePunkt
              key={stufe.feld}
              stufe={stufe}
              wert={werte[stufe.feld]}
              id={id}
              onAendern={onAendern}
            />
          ))}
        </div>
        <p className="calc-note">Status: {label ?? "noch nicht gesetzt"}</p>
      </>
    );
  }

  if (!zeitstrahl || zeitstrahl.stufen.length === 0) {
    return <p className="calc-note">Noch kein Zeitstrahl geladen.</p>;
  }

  return (
    <ul className="ship-stationen-liste">
      {zeitstrahl.stufen.map((stufe, index) => (
        <li
          key={stufe.id}
          className={index <= zeitstrahl.aktuelleStufeIndex ? "ship-station-erreicht" : "ship-station-offen"}
        >
          <span className="ship-station-label">{stufe.label}</span>
          <span className="ship-station-datum">{stufe.erreichtAm ? fmtDate(stufe.erreichtAm) : "—"}</span>
        </li>
      ))}
    </ul>
  );
}
